import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { useImmer } from 'use-immer'
import { UiBreadcrumb, UiDataTable } from 'components/common'
import { Box, Button, Flex, Heading, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text, useDisclosure } from '@chakra-ui/react'
import { deleteOpportunity, getOpportunities, exportOpportunities } from 'api'
import { Link as NavLink, useNavigate } from 'react-router-dom'
import { MdAdd } from 'react-icons/md'
import { useAppContext } from 'hooks/useAppContext'
import { format as formatDate } from 'date-fns'

const BreadcrumbNav = [
  {
    label: 'Opportunities',
    isCurrentPage: true
  }
]

const OPPORTUNITIES_COLUMNS = [
  {
    label: 'Name',
    orderBy: 'name',
    render: ({ name }) => name
  },
  {
    label: 'Sales User',
    orderBy: 'salesUserName',
    render: ({ salesUserName }) => salesUserName
  },
  {
    label: 'Company',
    orderBy: 'company',
    render: ({ company }) => company
  },
  {
    label: 'Customer',
    orderBy: 'customerCompany',
    render: ({ customerCompany }) => customerCompany
  },
  {
    label: 'Email',
    orderBy: 'email',
    render: ({ email }) => email
  },
  {
    label: 'Phone',
    orderBy: 'phone',
    render: ({ phone }) => phone ? ('+1 ' + phone) : ''
  },
  {
    label: 'Stage',
    orderBy: 'stage',
    render: ({ stage }) => stage
  },
  {
    label: 'Created At (Zoho)',
    orderBy: 'createdAtZoho',
    render: ({ createdAtZoho }) => {
      const date = new Date(createdAtZoho)
      return createdAtZoho ? formatDate(new Date(date.getTime() + date.getTimezoneOffset() * 60000), 'MM/dd/yyyy') : ''
    }
  },
  {
    label: 'Actions',
    render: ({ opportunities, notesCount, onRemove, onNotes, onOpportunities, ...rest }) => (
      <Flex columnGap={2}>
        <Button as={NavLink} to={`/opportunities/edit/${rest.id}`} size="xs" colorScheme="blue">Edit</Button>
        {/* <Button size="xs" colorScheme="red" onClick={() => onRemove(rest)}>Remove</Button> */}
      </Flex>
    )
  }
]

export const OpportunityList = () => {
  const navigate = useNavigate()
  const disclosureRemove = useDisclosure()
  const { createToast } = useAppContext()
  const [tableConfig, setTableConfig] = useImmer({
    curPage: 1,
    totalPages: 1,
    totalRecords: 0,
    itemsPerPage: 10,
    search: '',
    orderBy: {
      key: 'createdAtZoho',
      direction: 'desc'
    }
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)
  const [rows, setRows] = useState([])
  const [selectedOpportunity, setSelectedOpportunity] = useState(null)

  const load = async () => {
    setIsLoading(true)
    try {
      const response = await getOpportunities(
        tableConfig.curPage,
        tableConfig.itemsPerPage,
        tableConfig.search,
        tableConfig.orderBy.key,
        tableConfig.orderBy.direction
      )
      setRows(response.rows)
      setTableConfig(draft => {
        draft.totalPages = response.totalPages
        draft.totalRecords = response.totalRecords
      })
      setIsLoading(false)
    } catch (err) {
      console.log(err)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [tableConfig.curPage, tableConfig.itemsPerPage, tableConfig.search, tableConfig.orderBy.key, tableConfig.orderBy.direction])

  const onRemove = (opportunity) => {
    setSelectedOpportunity(opportunity)
    disclosureRemove.onOpen()
  }

  const handleRemove = async (opportunityId) => {
    setIsRemoving(true)
    try {
      await deleteOpportunity(opportunityId)
      setIsRemoving(false)
      disclosureRemove.onClose()
      load()
    } catch (err) {
      createToast('error', 'Error', err.response.data)
      setIsRemoving(false)
    }
  }

  const customizedRows = useMemo(() => {
    return rows.map(v => ({ ...v, onRemove }))
  }, [rows])

  return (
    <>
      <UiBreadcrumb items={BreadcrumbNav} />
      <Box>
        <Flex justifyContent="space-between" alignItems="center" my={8}>
          <Heading size="lg">Opportunities</Heading>
          <Button
            size="md"
            colorScheme="blue"
            onClick={() => navigate('/opportunities/add')}
            leftIcon={<MdAdd size="24px" color="white" />}
          >
            Add Opportunity
          </Button>
        </Flex>
        <UiDataTable
          tableConfig={tableConfig}
          updateTableConfig={setTableConfig}
          rows={customizedRows}
          rowKey="id"
          columns={OPPORTUNITIES_COLUMNS}
          isLoading={isLoading}
          exportConfig={{ filename: 'opportunities.csv', request: exportOpportunities }}
        />
      </Box>
      {disclosureRemove.isOpen && (
        <ModalRemoveOpportunity
          opportunity={selectedOpportunity}
          isRemoving={isRemoving}
          handleRemove={() => handleRemove(selectedOpportunity?.id)}
          {...disclosureRemove}
        />
      )}
    </>
  )
}

const ModalRemoveOpportunity = ({ opportunity, isRemoving, handleRemove, isOpen, onClose }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Remove Opportunity</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text>Are you sure to remove the opportunity - {opportunity?.name}?</Text>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme='red' mr={3} onClick={handleRemove} isLoading={isRemoving}>
            Remove
          </Button>
          <Button variant='ghost' onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

ModalRemoveOpportunity.propTypes = {
  opportunity: PropTypes.object,
  isRemoving: PropTypes.bool,
  handleRemove: PropTypes.func,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func
}
